import { useState } from "react";
import { format, parse, isValid } from "date-fns";
import { Calendar as CalendarIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

export function DatePicker({
  value,
  onChange,
  placeholder = "Pick a date",
  id,
  className,
  "data-testid": testId,
}) {
  const [open, setOpen] = useState(false);

  const dateValue = value
    ? parse(value, "yyyy-MM-dd", new Date())
    : undefined;
  const isValidDate = dateValue && isValid(dateValue);

  const handleSelect = (date) => {
    if (date) {
      onChange(format(date, "yyyy-MM-dd"));
    }
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen} modal={false}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          id={id}
          variant="outline"
          data-testid={testId}
          className={cn(
            "w-full justify-start text-left font-normal h-9",
            !isValidDate && "text-muted-foreground",
            className
          )}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setOpen((prev) => !prev);
          }}
        >
          <CalendarIcon className="mr-2 h-4 w-4 flex-shrink-0" />
          {isValidDate ? (
            format(dateValue, "dd MMM yyyy")
          ) : (
            <span>{placeholder}</span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-auto p-0 z-[9999]"
        align="start"
        side="bottom"
        sideOffset={4}
        onOpenAutoFocus={(e) => e.preventDefault()}
        onInteractOutside={(e) => {
          // Don't close if clicking inside a dialog
          const target = e.target;
          if (target?.closest?.("[role='dialog']")) {
            e.preventDefault();
          }
        }}
      >
        <Calendar
          mode="single"
          selected={isValidDate ? dateValue : undefined}
          onSelect={handleSelect}
          defaultMonth={isValidDate ? dateValue : new Date()}
        />
      </PopoverContent>
    </Popover>
  );
}
